// API client wiring: base URL and session-cookie credentials for the generated hey-api client, raw fetches and the uploader's XMLHttpRequests, W3C traceparent, the signed-out handler for 401 unauthenticated, the invalid-response handler for a response that broke the contract (the release watch then looks for a new release), ApiProblem errors, and every failed request remembered for feedback reports.
import { client } from "@/api/__generated__/client.gen";
import { ApiProblem, isAbortError, isApiProblem } from "@/api/problem";
import { recordProblem, traceIdOf } from "@/app/diagnostics";

const SIGNED_OUT_CODE = "unauthenticated";

function ignore(): void {
  return undefined;
}

let signedOutHandler: () => void = ignore;
let invalidResponseHandler: () => void = ignore;
let resolvedBaseUrl = "";
let requestCredentials: RequestCredentials = "same-origin";
let interceptorsInstalled = false;

function randomHex(bytes: number): string {
  const buffer = new Uint8Array(bytes);
  crypto.getRandomValues(buffer);
  return Array.from(buffer, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export function traceparent(): string {
  return `00-${randomHex(16)}-${randomHex(8)}-01`;
}

export function apiBaseUrl(): string {
  return resolvedBaseUrl;
}

export function apiUrl(path: string): string {
  return `${resolvedBaseUrl}${path}`;
}

export function sendsCredentialsCrossOrigin(): boolean {
  return requestCredentials === "include";
}

export function credentialsFor(baseUrl: string, pageOrigin: string): RequestCredentials {
  return new URL(baseUrl, pageOrigin).origin === new URL(pageOrigin).origin ? "same-origin" : "include";
}

function problemCode(body: unknown): unknown {
  return body !== null && typeof body === "object" && "code" in body ? body.code : null;
}

async function problemCodeOf(response: Response): Promise<unknown> {
  try {
    return problemCode(await response.clone().json());
  } catch {
    return null;
  }
}

async function reportSignedOut(response: Response): Promise<void> {
  if (response.status === 401 && (await problemCodeOf(response)) === SIGNED_OUT_CODE) {
    signedOutHandler();
  }
}

export function reportSignedOutBody(status: number, body: unknown): void {
  if (status === 401 && problemCode(body) === SIGNED_OUT_CODE) {
    signedOutHandler();
  }
}

function installInterceptors(): void {
  if (interceptorsInstalled) {
    return;
  }
  interceptorsInstalled = true;
  client.interceptors.request.use((request) => {
    if (!request.headers.has("traceparent")) {
      request.headers.set("traceparent", traceparent());
    }
    return request;
  });
  client.interceptors.response.use(async (response) => {
    await reportSignedOut(response);
    return response;
  });
  client.interceptors.error.use((error, response, request) => {
    if (isAbortError(error)) {
      return error;
    }
    const problem = isApiProblem(error) ? error : problemOf(error, response);
    recordProblem({
      method: request?.method ?? "GET",
      url: request?.url ?? "",
      status: problem.status,
      code: problem.code,
      traceId: problem.trace_id ?? traceIdOf(request?.headers.get("traceparent") ?? null),
    });
    return problem;
  });
}

function problemOf(error: unknown, response: Response | undefined): ApiProblem {
  if (response === undefined) {
    return ApiProblem.network(error);
  }
  if (!response.ok) {
    return ApiProblem.fromBody(response.status, error, response.headers);
  }
  reportInvalidResponse();
  return ApiProblem.invalidResponse(error);
}

export function setSignedOutHandler(handler: () => void): () => void {
  signedOutHandler = handler;
  return () => {
    if (signedOutHandler === handler) {
      signedOutHandler = ignore;
    }
  };
}

export function setInvalidResponseHandler(handler: () => void): () => void {
  invalidResponseHandler = handler;
  return () => {
    if (invalidResponseHandler === handler) {
      invalidResponseHandler = ignore;
    }
  };
}

export function reportInvalidResponse(): void {
  invalidResponseHandler();
}

export function configureApiClient(baseUrl: string): void {
  resolvedBaseUrl = (baseUrl || window.location.origin).replace(/\/+$/u, "");
  requestCredentials = credentialsFor(resolvedBaseUrl, window.location.origin);
  client.setConfig({ baseUrl: resolvedBaseUrl, credentials: requestCredentials });
  installInterceptors();
}

export function apiHeaders(init?: HeadersInit): Headers {
  const headers = new Headers(init);
  if (!headers.has("traceparent")) {
    headers.set("traceparent", traceparent());
  }
  return headers;
}

export async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  const headers = apiHeaders(init?.headers);
  const failed = { method: init?.method ?? "GET", url: path, traceId: traceIdOf(headers.get("traceparent")) };
  let response: Response;
  try {
    response = await fetch(apiUrl(path), {
      ...init,
      headers,
      credentials: requestCredentials,
    });
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    const problem = ApiProblem.network(error);
    recordProblem({ ...failed, status: problem.status, code: problem.code });
    throw problem;
  }
  await reportSignedOut(response);
  if (!response.ok) {
    const code = await problemCodeOf(response);
    recordProblem({
      ...failed,
      status: response.status,
      code: typeof code === "string" ? code : `http-${response.status}`,
    });
  }
  return response;
}
